import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialogModule } from '@angular/material/dialog';

import { ProjectService } from './project.service';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import {
  ProjectDto,
  PROJECT_STATUS_LABELS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_CATEGORIES,
  ProjectListParams
} from './project.models';
import { healthRagClass } from '../shared/health.utils';

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatChipsModule,
    MatPaginatorModule,
    MatMenuModule,
    MatDividerModule,
    MatDialogModule,
    SkeletonComponent
  ],
  templateUrl: './projects.component.html',
  styleUrl: './projects.component.scss'
})
export class ProjectsComponent implements OnInit, OnDestroy {
  private readonly projectService = inject(ProjectService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly projects = signal<ProjectDto[]>([]);
  readonly totalCount = signal(0);

  // Filter state
  searchQuery = '';
  statusFilter: number | '' = '';
  categoryFilter = '';
  accountIdFilter = '';
  activeOnlyFilter = false;
  favoritesOnlyFilter = false;
  templatesOnlyFilter = false;

  // Pagination
  page = 0;
  pageSize = 12;

  readonly statusOptions = Object.entries(PROJECT_STATUS_LABELS).map(([value, label]) => ({
    value: Number(value),
    label
  }));

  readonly statusLabels = PROJECT_STATUS_LABELS;
  readonly priorityLabels = PROJECT_PRIORITY_LABELS;
  readonly categoryOptions = PROJECT_CATEGORIES;

  readonly statusColorMap: Record<number, string> = {
    0: 'status-planning',
    1: 'status-active',
    2: 'status-onhold',
    3: 'status-completed',
    4: 'status-cancelled',
    5: 'status-archived'
  };

  readonly priorityColorMap: Record<number, string> = {
    0: 'priority-low',
    1: 'priority-medium',
    2: 'priority-high',
    3: 'priority-critical'
  };

  ngOnInit(): void {
    this.accountIdFilter = this.route.snapshot.queryParamMap.get('accountId') ?? '';

    this.searchSubject.pipe(
      debounceTime(350),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.page = 0;
      this.loadProjects();
    });

    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.loading.set(true);
    this.error.set(null);

    const params: ProjectListParams = {
      page: this.page + 1,
      pageSize: this.pageSize,
      search: this.searchQuery || undefined,
      status: this.statusFilter !== '' ? this.statusFilter : undefined,
      category: this.categoryFilter || undefined,
      activeOnly: this.activeOnlyFilter || undefined,
      favoritesOnly: this.favoritesOnlyFilter || undefined,
      templatesOnly: this.templatesOnlyFilter || undefined
    };

    this.projectService.list(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.projects.set(result.items ?? []);
        this.totalCount.set(result.totalCount ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load projects.');
        this.loading.set(false);
      }
    });
  }

  onSearch(value: string): void {
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  onStatusChange(): void {
    this.page = 0;
    this.loadProjects();
  }

  onActiveOnlyChange(): void {
    this.page = 0;
    this.loadProjects();
  }

  onCategoryChange(): void {
    this.page = 0;
    this.loadProjects();
  }

  onFavoritesOnlyChange(): void {
    this.page = 0;
    this.loadProjects();
  }

  onTemplatesOnlyChange(): void {
    this.page = 0;
    this.loadProjects();
  }

  toggleFavorite(project: ProjectDto, event: Event): void {
    event.stopPropagation();
    this.projectService.toggleFavorite(project.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadProjects(),
      error: (err) => this.error.set(err?.message || 'Failed to update favorite.')
    });
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProjects();
  }

  openProject(project: ProjectDto): void {
    this.router.navigate(['/projects', project.id]);
  }

  /** Opens the project Tasks tab with the create-task form expanded. */
  addTask(project: ProjectDto, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/projects', project.id], {
      queryParams: { tab: 'tasks', addTask: '1' }
    });
  }

  createNewProject(): void {
    this.router.navigate(['/projects/new']);
  }

  editProject(project: ProjectDto, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/projects', project.id, 'edit']);
  }

  deleteProject(project: ProjectDto, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete project "${project.name}"? This cannot be undone.`)) return;

    this.projectService.delete(project.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadProjects(),
      error: (err) => this.error.set(err?.message || 'Failed to delete project.')
    });
  }

  duplicateProject(project: ProjectDto, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Duplicate project "${project.name}"?`)) return;

    this.projectService.duplicate(project.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (created) => this.router.navigate(['/projects', created.id]),
      error: (err) => this.error.set(err?.message || 'Failed to duplicate project.')
    });
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.statusFilter = '';
    this.categoryFilter = '';
    this.activeOnlyFilter = false;
    this.favoritesOnlyFilter = false;
    this.templatesOnlyFilter = false;
    this.page = 0;
    this.loadProjects();
  }

  getProgressColor(progress: number): string {
    if (progress >= 80) return 'success';
    if (progress >= 40) return 'warning';
    return 'danger';
  }

  trackById(_index: number, item: ProjectDto): string {
    return item.id;
  }
}
